import {createRef, useEffect, useState} from "react"
import {useNavigate, Link} from "react-router-dom"
import {useAuth} from "../../../../app/auth/auth"
import {useAppSelector} from "../../../../app/hooks"
import {useCurrency} from "../../../../app/store/store"
import fetchWithRetry, {withLanguage} from "../../../../util/request"
import Currency from "../../currency/Currency"
import Button from "../../../../component/button/Button"
import SwitchInput from "../../../../component/switch/SwitchInput"
import Text from "../../../../component/text/Text"
import {Icon} from "../../../../component/icon/Icon"
import {IconX} from "@tabler/icons-react"
import "./GoalContributionModal.css"
import {Trans, useTranslation} from "react-i18next"

interface PaywallInfo {
    name: string
    id: string
    paywall: string
    icon: string
    enabled: boolean
    reason: string
}

const EMAIL_REGEX = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/

export default function GoalContributionModal(props: {
    currencies: { [key: string]: Currency }
    close: () => void
}) {
    const {currencies, close} = props;
    const {t, i18n} = useTranslation();

    const auth = useAppSelector(useAuth)
    const currencyName = useAppSelector(useCurrency)
    const currency: Currency | undefined = currencies[currencyName];

    const navigate = useNavigate()

    const [amount, setAmount] = useState<number>(0)
    const [anonymous, setAnonymous] = useState(false)
    const [vop, setVop] = useState(false)

    const emailInput = {
        id: "email",
        label: t("checkout.inputs.email"),
        regex: EMAIL_REGEX,
        inputRef: createRef<HTMLInputElement>()
    }

    const [paymentOptions, setPaymentOptions] = useState<PaywallInfo[] | null>(null)
    const [selectedPaymentOption, setSelectedPaymentOption] =
        useState<null | {
            id: string
            paywall: string
        }>(null)

    const [error, setError] = useState<string | undefined>()
    const [sending, setSending] = useState(false)

    useEffect(() => {
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.setProperty("overflow", null);
        }
    })

    useEffect(() => {
        if (!currency || !Number.isInteger(amount) || amount <= 0) {
            setPaymentOptions(null)
            return;
        }
        const total = amount * currency.multiplied;
        return fetchWithRetry(async res => {
            if (!res.ok) {
                setPaymentOptions(null)
                return false;
            }
            let body = (await res.json()) as PaywallInfo[]
            setPaymentOptions(body)
            return true;
        }, `${process.env.REACT_APP_API_BASE}/store/paywall/list/${currencyName}/${total}`);
    }, [amount, currency, currencyName, i18n.language])

    if (!auth.user) {
        setTimeout(() => navigate("/login?return=/store"))
        return null;
    }

    function sendForm() {
        if (sending) return;        if (!Number.isInteger(amount) || amount <= 0) {
            setError(t("goal.errors.amountRequired"))
            return;
        }
        const email = emailInput.inputRef.current?.value ?? "";
        if (!EMAIL_REGEX.test(email)) {
            setError(t("goal.errors.emailInvalid"))
            emailInput.inputRef.current?.classList.add("invalid")
            return;
        }
        if (!selectedPaymentOption?.paywall) {
            setError(t("goal.errors.paymentMethodRequired"))
            return;
        }
        if (!vop) {
            setError(t("goal.errors.vopRequired"))
            return;
        }
        setError(undefined)

        const token = auth.token;
        if (!token) return;

        setSending(true)

        fetch(process.env.REACT_APP_API_BASE + "/store/goal/contribute", withLanguage({
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": token,
                "Return-Hostname": window.location.origin
            },
            body: JSON.stringify({
                amount: amount,
                currency: currencyName,
                paywall: selectedPaymentOption.paywall,
                anonymous: anonymous,
                email: email
            })
        })).then(res => {
            if (res.ok) {
                res.text().then(url => {
                    window.location.href = url;
                })
            } else {
                setSending(false)
                res.json().then(body => setError(body.message))
            }
        }).catch(() => setSending(false))
    }

    return <div className={"goal-contribution-modal-container"}>
        <IconX className={"close"} onClick={close}/>
        <div className={"goal-contribution-modal"} onKeyDown={e => {
            if (e.code === "Enter") sendForm()
        }}>
            <h3>{t("goal.modalTitle")}</h3>
            <p className={"prompt"}>{t("goal.prompt")}</p>

            <div className={"input-field"}>
                <label htmlFor={"amount"} className={"label"}>{t("goal.amountLabel")}{currency ? ` (${currency.symbol.trim()})` : ""}</label>
                <input type={"number"} name={"amount"} min={1} step={1} value={amount > 0 ? amount : ""}
                       onInput={e => {
                           const value = parseInt(e.currentTarget.value);
                           setAmount(Number.isNaN(value) ? 0 : value);
                       }}/>
            </div>

            <div className={"input-field"}>
                <label htmlFor={emailInput.id} className={"label"}>{emailInput.label}</label>
                <input type={"text"} name={emailInput.id} ref={emailInput.inputRef} onInput={e => {
                    const value = e.currentTarget.value;
                    if (!EMAIL_REGEX.test(value)) {
                        e.currentTarget.classList.add("invalid")
                    } else {
                        e.currentTarget.classList.remove("invalid")
                    }
                }}/>
            </div>

            <SwitchInput label={t("goal.anonymousLabel")} defaultValue={anonymous} callback={setAnonymous}/>

            <div className={"payment-options"}>
                <p className={"label"}>{t("goal.paymentMethodLabel")}</p>
                {Number.isInteger(amount) && amount > 0 ? (
                    paymentOptions ? <div className={"paywall-list"}>
                        {paymentOptions.map(paywall => <div
                            key={paywall.id}
                            className={`paywall ${paywall.enabled ? "enabled" : "disabled"}${paywall.id === selectedPaymentOption?.id ? " selected" : ""}`}
                            onClick={() => {
                                if (paywall.enabled) {
                                    setSelectedPaymentOption({
                                        id: paywall.id,
                                        paywall: paywall.paywall
                                    })
                                }
                            }}>
                            <Icon iconName={paywall.icon}/>
                            <div className={"info"}>
                                <p className={"name"}>{paywall.name}</p>
                                {paywall.reason ? <p className={"reason"}>{paywall.reason}</p> : null}
                            </div>
                        </div>)}
                    </div> : <div className={"loader center margin"}/>
                ) : <p className={"hint"}>{t("goal.enterAmountFirst")}</p>}
            </div>

            <SwitchInput label={<Trans i18nKey={"checkout.agreeVop"} components={{
                link: <Link to={"/vop"} target={"_blank"}/>
            }}/>} defaultValue={vop} callback={setVop}/>

            {error ? <Text color={"red"}>{error}</Text> : null}
            <Button type={"green"} loading={sending} onClick={sendForm}>{t("goal.contribute")}</Button>
        </div>
    </div>;
}
